import React, { useState, useEffect } from 'react';
import { Modal, Button, Label, TextInput, Select, Checkbox, Alert } from 'flowbite-react';
import { HiPhone } from 'react-icons/hi';
import type { PersonPhoneAssociation } from '../../types/api';

interface EditPersonPhoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  phone?: PersonPhoneAssociation | null;  // null for new phone
  phoneTypes?: Array<{ value: number; label: string }>;  // Available phone types from descriptors
  onSave: (phoneData: Partial<PersonPhoneAssociation>) => Promise<void>;
}

export const EditPersonPhoneModal: React.FC<EditPersonPhoneModalProps> = ({
  isOpen,
  onClose,
  phone,
  phoneTypes = [],
  onSave
}) => {
  const [formData, setFormData] = useState({
    phoneNumber: '',
    extension: '',
    phoneTypeDescriptorId: null as number | null,
    primaryPhoneIndicator: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (phone) {
      setFormData({
        phoneNumber: phone.phoneNumber || '',
        extension: phone.extension || '',
        phoneTypeDescriptorId: phone.phoneTypeDescriptorId || null,
        primaryPhoneIndicator: phone.primaryPhoneIndicator || false
      });
    } else {
      // Reset for new phone
      setFormData({
        phoneNumber: '',
        extension: '',
        phoneTypeDescriptorId: null,
        primaryPhoneIndicator: false
      });
    }
  }, [phone]);

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX for US numbers
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else if (digits.length <= 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else {
      // Don't format if more than 10 digits (international numbers)
      return value;
    }
  };

  const handlePhoneChange = (value: string) => {
    // Allow international formats by checking if it starts with '+'
    if (value.startsWith('+')) {
      setFormData({ ...formData, phoneNumber: value });
    } else {
      setFormData({ ...formData, phoneNumber: formatPhoneNumber(value) });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Basic phone validation (allow various formats)
    const phoneRegex = /^[\d\s\-\(\)\+\.]+$/;
    if (!phoneRegex.test(formData.phoneNumber) || formData.phoneNumber.replace(/\D/g, '').length < 10) {
      setError('Please enter a valid phone number (at least 10 digits)');
      setLoading(false);
      return;
    }

    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save phone');
    } finally {
      setLoading(false);
    }
  };

  const isEditMode = !!phone;

  return (
    <Modal show={isOpen} onClose={onClose} dismissible size="md">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <HiPhone className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900">
            {isEditMode ? 'Edit Phone Number' : 'Add Phone Number'}
          </h3>
        </div>

        {error && (
          <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <Label htmlFor="phoneNumber" className="block mb-2 text-sm font-medium text-gray-700">
                Phone Number <span className="text-red-500">*</span>
              </Label>
              <TextInput
                id="phoneNumber"
                type="tel"
                placeholder="(555) 555-5555"
                value={formData.phoneNumber}
                onChange={(e) => handlePhoneChange(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="extension" className="block mb-2 text-sm font-medium text-gray-700">
                Extension
              </Label>
              <TextInput
                id="extension"
                type="text"
                placeholder="1234"
                value={formData.extension}
                onChange={(e) => setFormData({ ...formData, extension: e.target.value })}
                disabled={loading}
              />
            </div>
          </div>

          {phoneTypes.length > 0 && (
            <div>
              <Label htmlFor="phoneType" className="block mb-2 text-sm font-medium text-gray-700">
                Phone Type
              </Label>
              <Select
                id="phoneType"
                value={formData.phoneTypeDescriptorId || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  phoneTypeDescriptorId: e.target.value ? Number(e.target.value) : null
                })}
                disabled={loading}
              >
                <option value="">Select type...</option>
                {phoneTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div className="checkbox-wrapper">
            <Checkbox
              id="primaryPhone"
              checked={formData.primaryPhoneIndicator}
              onChange={(e) => setFormData({ ...formData, primaryPhoneIndicator: e.target.checked })}
              disabled={loading}
            />
            <Label htmlFor="primaryPhone" className="text-sm font-medium text-gray-700">
              Set as primary phone number
            </Label>
          </div>

          {formData.primaryPhoneIndicator && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                Setting this as primary will automatically unset any existing primary phone.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button color="gray" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : isEditMode ? 'Save Changes' : 'Add Phone'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};